import type {
	FuelCalculationType,
	MenuItem,
	SpeedTimeDistanceType,
	UnitConversionType,
	WeightBalanceType,
	WindTriangleType,
} from "../types";

export const MENU_OPTIONS = {
	unit_conversions: [
		{ type: "altitude" as UnitConversionType, label: "Altitude" },
		{ type: "cloud_base" as UnitConversionType, label: "Cloud Base" },
		{
			type: "standard_atmosphere" as UnitConversionType,
			label: "Standard Atmosphere",
		},
		{ type: "airspeed" as UnitConversionType, label: "Airspeed" },
		{ type: "fuel" as UnitConversionType, label: "Fuel" },
		{ type: "ground_speed" as UnitConversionType, label: "Ground Speed" },
	] as MenuItem<UnitConversionType>[],
	speed_time_distance: [
		{ type: "speed" as SpeedTimeDistanceType, label: "Speed" },
		{ type: "time" as SpeedTimeDistanceType, label: "Time" },
		{ type: "distance" as SpeedTimeDistanceType, label: "Distance" },
		{ type: "fuel_flow" as SpeedTimeDistanceType, label: "Fuel Flow" },
		{ type: "fuel_required" as SpeedTimeDistanceType, label: "Fuel Required" },
	] as MenuItem<SpeedTimeDistanceType>[],
	fuel_calculations: [
		{
			type: "gallons_to_pounds" as FuelCalculationType,
			label: "Gallons to Pounds",
		},
		{
			type: "pounds_to_gallons" as FuelCalculationType,
			label: "Pounds to Gallons",
		},
		{ type: "fuel_density" as FuelCalculationType, label: "Fuel Density" },
		{ type: "total_fuel" as FuelCalculationType, label: "Total Fuel" },
		{ type: "fuel_remaining" as FuelCalculationType, label: "Fuel Remaining" },
	] as MenuItem<FuelCalculationType>[],
	wind_triangle: [
		{ type: "true_airspeed" as WindTriangleType, label: "True Airspeed" },
		{ type: "ground_speed" as WindTriangleType, label: "Ground Speed" },
		{ type: "wind_speed" as WindTriangleType, label: "Wind Speed" },
		{ type: "wind_direction" as WindTriangleType, label: "Wind Direction" },
		{ type: "track" as WindTriangleType, label: "Track" },
	] as MenuItem<WindTriangleType>[],
	weight_balance: [
		{ type: "weight" as WeightBalanceType, label: "Weight" },
		{ type: "moment" as WeightBalanceType, label: "Moment" },
		{ type: "cg_location" as WeightBalanceType, label: "CG Location" },
		{ type: "cg_limits" as WeightBalanceType, label: "CG Limits" },
		{ type: "max_weight" as WeightBalanceType, label: "Max Weight" },
	] as MenuItem<WeightBalanceType>[],
};

export const MODE_NAMES = {
	unit_conversions: "Unit Conversions",
	speed_time_distance: "Speed Time Distance",
	fuel_calculations: "Fuel Calculations",
	wind_triangle: "Wind Triangle",
	weight_balance: "Weight & Balance",
};

export const performUnitConversion = (
	value: number,
	subMode: UnitConversionType,
): string => {
	switch (subMode) {
		case "altitude": {
			return `${(value * 0.3048).toFixed(2)} meters`;
		}
		case "airspeed": {
			return `${(value * 1.15078).toFixed(2)} mph`;
		}
		case "fuel": {
			return `${(value * 3.78541).toFixed(2)} liters`;
		}
		case "ground_speed": {
			return `${(value * 1.852).toFixed(2)} km/h`;
		}
		case "cloud_base": {
			const tempDiff = value;
			const cloudBase = tempDiff * 400;
			return `${cloudBase.toFixed(0)} feet AGL`;
		}
		case "standard_atmosphere": {
			const pressureAltitude = value * 30;
			return `${pressureAltitude.toFixed(0)} feet`;
		}
		default: {
			return "";
		}
	}
};

export const performSpeedTimeDistance = (
	value: number,
	selectedIndex: number,
): string => {
	const selectedItem = MENU_OPTIONS.speed_time_distance[selectedIndex];
	if (!selectedItem) {
		return "";
	}

	switch (selectedItem.type) {
		case "speed": {
			return `${(value * 1.15078).toFixed(2)} mph`;
		}
		case "time": {
			return `${(value / 60).toFixed(2)} hours`;
		}
		case "distance": {
			return `${(value * 1.852).toFixed(2)} km`;
		}
		case "fuel_flow": {
			return `${(value * 3.78541).toFixed(2)} L/hr`;
		}
		case "fuel_required": {
			return `${(value * 6.8).toFixed(2)} lbs`;
		}
		default: {
			return "";
		}
	}
};

export const performFuelCalculations = (
	value: number,
	selectedIndex: number,
): string => {
	const selectedItem = MENU_OPTIONS.fuel_calculations[selectedIndex];
	if (!selectedItem) {
		return "";
	}

	switch (selectedItem.type) {
		case "gallons_to_pounds": {
			const avGasWeight = 6.0;
			return `${(value * avGasWeight).toFixed(2)} lbs`;
		}
		case "pounds_to_gallons": {
			const avGasWeight = 6.0;
			return `${(value / avGasWeight).toFixed(2)} gal`;
		}
		case "fuel_density": {
			return `${(value * 0.72).toFixed(2)} kg/L`;
		}
		case "total_fuel": {
			const usableFactor = 0.95;
			return `${(value * usableFactor).toFixed(2)} usable`;
		}
		case "fuel_remaining": {
			const burnRate = 8.5;
			return `${(value / burnRate).toFixed(2)} hrs remaining`;
		}
		default: {
			return "";
		}
	}
};

export const performWindTriangle = (
	value: number,
	selectedIndex: number,
): string => {
	const selectedItem = MENU_OPTIONS.wind_triangle[selectedIndex];
	if (!selectedItem) {
		return "";
	}

	switch (selectedItem.type) {
		case "true_airspeed": {
			return `${(value * 1.02).toFixed(2)} KTAS`;
		}
		case "ground_speed": {
			return `${(value * 0.95).toFixed(2)} knots`;
		}
		case "wind_speed": {
			return `${(value).toFixed(0)} knots`;
		}
		case "wind_direction": {
			return `${(value).toFixed(0)}°`;
		}
		case "track": {
			return `${(value).toFixed(0)}° magnetic`;
		}
		default: {
			return "";
		}
	}
};

export const performWeightBalance = (
	value: number,
	selectedIndex: number,
): string => {
	const selectedItem = MENU_OPTIONS.weight_balance[selectedIndex];
	if (!selectedItem) {
		return "";
	}

	switch (selectedItem.type) {
		case "weight": {
			return `${(value).toFixed(2)} lbs`;
		}
		case "moment": {
			return `${(value).toFixed(2)} lb-in`;
		}
		case "cg_location": {
			return `${(value).toFixed(2)} inches`;
		}
		case "cg_limits": {
			return value > 85 ? "AFT LIMIT" : "WITHIN LIMITS";
		}
		case "max_weight": {
			return `${(value).toFixed(0)} lbs MTOW`;
		}
		default: {
			return "";
		}
	}
};
